import { useMutation } from '@tanstack/react-query';

import {
  SlideGroupData,
  SlideGroupsMap,
  useUnitData,
} from '@/features/_data/useUnitData';
import { TOASTTYPE, useToast } from '@/ui/Toast/Toast';
import { fetcher } from '@/utils/fetcher';
import { RETAIL_API_URL } from 'src/utils/envConfig';
import { useIntl } from 'react-intl';
import { MagicModel } from '@/types/MagicModel';
import { buildUpdatePayload, getDiff } from '@/utils/slideEditorUtils';
import { Unit } from '@/types/Unit';
import { FrontendSlide, StoredSlide } from '@/types/SlideType';
import { getWindowLogger } from '@/utils/remoteLogger';
import { LogAction, LogStatus } from '@/types/Logging';
import { useCurrentAuthSession } from '@/context/AuthContext';
import { fsPath } from './__path';
import { useUserInfo } from './useUserInfo';

const logger = getWindowLogger({
  file: `${fsPath}/useUpdateSlides.ts`,
});

export const useUpdateSlides = (unitId: string | undefined) => {
  const { user } = useCurrentAuthSession();
  const { data: userInfo } = useUserInfo();
  const toast = useToast();
  const intl = useIntl();
  const { data: unitWithDerivedData } = useUnitData(unitId);
  const {
    mutateAsync: updateSlideGroups,
    isPending: isLoading,
    error,
  } = useMutation({
    mutationFn: async ({
      newSlideGroups,
      newMagicModels,
      newChannel,
      disallowSlideDeletion,
      languageSupport,
      homeTimer,
      librarySlides,
    }: {
      newSlideGroups: SlideGroupsMap | null;
      newMagicModels?: MagicModel[];
      newChannel?: boolean;
      disallowSlideDeletion?: boolean;
      languageSupport?: boolean;
      homeTimer?: number;
      librarySlides: StoredSlide[];
    }) => {
      if (
        !unitId ||
        !unitWithDerivedData?.derivedData.slideGroups ||
        !newSlideGroups
      ) {
        toast({
          title: intl.formatMessage({
            defaultMessage: 'Something went wrong updating the configuration',
            id: 'gMGjz1',
          }),
          mode: TOASTTYPE.Danger,
        });
        return undefined;
      }

      const {
        changedSlides,
        createdSlides,
        deletedSlides,
        changedOrderSlides,
        changedSlideGroups,
        detectAddedSlideGroups,
        isMagicModelListChanged,
        changedChannel,
        changedDisallowSlideDeletion,
        changedLanguageSupport,
        changedHomeTimer,
      } = getDiff(
        unitWithDerivedData.derivedData.slideGroups,
        newSlideGroups,
        unitWithDerivedData.nfcTagIdList,
        newMagicModels,
        unitWithDerivedData.selectedMappingTag,
        newChannel,
        unitWithDerivedData.slideDeleteDisallowed,
        disallowSlideDeletion,
        unitWithDerivedData.languageSupport,
        languageSupport,
        unitWithDerivedData.homeTimer,
        homeTimer,
      );

      if (
        !changedSlides.length &&
        !createdSlides.length &&
        !deletedSlides.length &&
        !changedOrderSlides.length &&
        !changedSlideGroups.length &&
        !detectAddedSlideGroups.length &&
        !isMagicModelListChanged &&
        !changedChannel &&
        !changedDisallowSlideDeletion &&
        !changedLanguageSupport &&
        !changedHomeTimer
      ) {
        toast({
          title: intl.formatMessage({
            defaultMessage: 'No changes detected.',
            id: 'SnE6J3',
          }),
          subtitle: intl.formatMessage({
            defaultMessage: 'Nothing has been updated.',
            id: 'hrUy+n',
          }),
          mode: TOASTTYPE.Info,
        });
        return undefined;
      }

      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { derivedData, ...unitData } = unitWithDerivedData;

      const payload = await buildUpdatePayload(
        unitData,
        newSlideGroups,
        createdSlides,
        changedSlides,
        librarySlides,
        newMagicModels,
        newChannel,
        disallowSlideDeletion,
        languageSupport,
        homeTimer,
      );

      const result = fetcher<Unit>(`${RETAIL_API_URL}/units/update`, {
        method: 'POST',
        body: JSON.stringify(payload),
      });

      const logNewGroups = Object.values(newSlideGroups).map(
        (group: SlideGroupData) => ({
          id: group.id,
        }),
      );

      const logCreatedSlides = Object.values(createdSlides).map(
        (slide: FrontendSlide) => ({
          id: slide.id ?? slide.slideId ?? 'no-id',
        }),
      );

      const logChangedSlides = Object.values(changedSlides).map(
        (slide: FrontendSlide) => ({
          id: slide.id ?? slide.slideId ?? 'no-id',
        }),
      );

      const logNewMagicModels = (newMagicModels ?? []).map(
        (model: MagicModel) => ({
          sensorId: model.sensorId,
        }),
      );

      logger.info({
        action: LogAction.UPDATE,
        status: LogStatus.SUCCEEDED,
        user: user?.uid ?? 'unkown-user',
        customer: userInfo?.customer ?? 'unkown-customer',
        data: {
          newSlideGroups: logNewGroups,
          createdSlides: logCreatedSlides,
          changedSlides: logChangedSlides,
          newMagicModels: logNewMagicModels,
        },
      });

      return result;
    },
    onSuccess: () => {
      toast({
        mode: TOASTTYPE.Saved,
      });
    },
    onError: () => {
      toast({
        title: intl.formatMessage({
          defaultMessage: 'Something went wrong updating the configuration.',
          id: 'ZsVFqH',
        }),
        mode: TOASTTYPE.Danger,
      });
    },
  });

  return {
    updateSlideGroups,
    isLoading,
    error,
  };
};
